// qs-util-imageProcessor.ts
import type {
  FileValidationResult,
  ImageProcessingOptions,
  SupportedImageFormat
} from '@/types/qs-type-customImage'
import {
  DEFAULT_IMAGE_QUALITY,
  MAX_FILE_SIZE,
  MAX_IMAGE_DIMENSION,
  SUPPORTED_IMAGE_FORMATS
} from '@/types/qs-type-customImage'

export function validateImageFile(file: File): FileValidationResult {
  if (!SUPPORTED_IMAGE_FORMATS.includes(file.type as SupportedImageFormat)) {
    return {
      isValid: false,
      error: 'Please select a PNG, WEBP, or JPG image'
    }
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `Image file is too large. Maximum size is ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB`,
      fileSize: file.size
    }
  }

  return {
    isValid: true,
    fileSize: file.size
  }
}

export function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Unable to process image file. Please try another image'))
    }

    img.src = url
  })
}

export function calculateTargetDimensions(
  originalWidth: number,
  originalHeight: number,
  maxDimension: number = MAX_IMAGE_DIMENSION
): { width: number; height: number } {
  if (originalWidth <= maxDimension && originalHeight <= maxDimension) {
    return { width: originalWidth, height: originalHeight }
  }

  const aspectRatio = originalWidth / originalHeight

  if (originalWidth > originalHeight) {
    return {
      width: maxDimension,
      height: Math.round(maxDimension / aspectRatio)
    }
  } else if (originalHeight > originalWidth) {
    return {
      width: Math.round(maxDimension * aspectRatio),
      height: maxDimension
    }
  } else {
    return {
      width: maxDimension,
      height: maxDimension
    }
  }
}

export function scaleImageToDataUrl(
  image: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  options: Partial<ImageProcessingOptions> = {}
): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      canvas.width = targetWidth
      canvas.height = targetHeight

      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

      const format = options.format || 'png'
      const quality = options.quality || DEFAULT_IMAGE_QUALITY

      const dataUrl = canvas.toDataURL(`image/${format}`, quality)

      canvas.remove()

      resolve(dataUrl)
    } catch (error) {
      reject(new Error('Image processing failed. Please try again'))
    }
  })
}

export function generateThumbnailPreview(
  dataUrl: string,
  thumbnailSize: number = 64
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Canvas context not available'))
          return
        }

        canvas.width = thumbnailSize
        canvas.height = thumbnailSize

        const size = Math.min(img.width, img.height)
        const x = (img.width - size) / 2
        const y = (img.height - size) / 2

        ctx.drawImage(
          img,
          x, y, size, size,
          0, 0, thumbnailSize, thumbnailSize
        )

        const thumbnailDataUrl = canvas.toDataURL('image/png', 0.8)

        canvas.remove()

        resolve(thumbnailDataUrl)
      } catch (error) {
        reject(new Error('Thumbnail generation failed'))
      }
    }

    img.onerror = () => {
      reject(new Error('Invalid image data'))
    }

    img.src = dataUrl
  })
}

export async function processImageFile(file: File): Promise<{
  dataUrl: string
  preview: string
  originalDimensions: { width: number; height: number }
  processedDimensions: { width: number; height: number }
}> {
  const validation = validateImageFile(file)
  if (!validation.isValid) {
    throw new Error(validation.error)
  }

  const image = await loadImageFromFile(file)

  const originalDimensions = { width: image.width, height: image.height }
  const processedDimensions = calculateTargetDimensions(image.width, image.height)

  const dataUrl = await scaleImageToDataUrl(
    image,
    processedDimensions.width,
    processedDimensions.height
  )

  const preview = await generateThumbnailPreview(dataUrl)

  return {
    dataUrl,
    preview,
    originalDimensions,
    processedDimensions
  }
}

export function createFileInput(): HTMLInputElement {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = SUPPORTED_IMAGE_FORMATS.join(',')
  input.style.display = 'none'
  return input
}

export function openFileDialog(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = createFileInput()

    input.onchange = (event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0] || null
      document.body.removeChild(input)
      resolve(file)
    }

    input.oncancel = () => {
      document.body.removeChild(input)
      resolve(null)
    }

    document.body.appendChild(input)
    input.click()
  })
}